import { useCallback, useSyncExternalStore } from 'react';
import { readFrameOrderStats, subscribeFrameOrder } from 'utils/frameOrder';

const LATE_COLOR = '#f87171';

/**
 * EN-11178: Performance HUD row counting this table's frames that arrived out
 * of order (utils/frameOrder.ts) this session -- older than a frame the table
 * had already shown. "cross-hand" ones are from an earlier hand: the flashback
 * PACECHAIN must not cause. "dropped" is how many were not applied (late ones
 * and repeats, with release.table-frame-order on); "valve" is how often a frame
 * read older than the newest showed the table further along (the database clock
 * stepped back), so it was applied instead of the table freezing.
 */
const FrameOrderRow = ({ gameID }: { gameID: string }) => {
  const getStats = useCallback(() => readFrameOrderStats(gameID), [gameID]);
  const stats = useSyncExternalStore(subscribeFrameOrder, getStats);

  return (
    <div
      data-testid='perf-hud-frame-order'
      title={
        'Out-of-order table frames (EN-11178): frames that arrived older than one this ' +
        'table had already shown, this session. "cross-hand" = from an earlier hand. ' +
        '"dropped" = not applied (release.table-frame-order on). "valve" = an older frame ' +
        'showing a later state was applied (the database clock stepped back). Frames without a ' +
        'version (older servers) are not counted.'
      }>
      frame order{' '}
      {!stats ? (
        <span style={{ opacity: 0.6 }}>no versioned frames</span>
      ) : stats.outOfOrder === 0 && stats.dropped === 0 ? (
        <span style={{ opacity: 0.6 }}>in order ({stats.checked})</span>
      ) : (
        <span style={{ color: LATE_COLOR }}>
          {stats.outOfOrder} late / {stats.checked}
          {stats.crossHand > 0 ? `, ${stats.crossHand} cross-hand` : ''}
          {stats.dropped > 0 ? `, ${stats.dropped} dropped` : ''}
          {stats.valveOpened > 0 ? `, valve ${stats.valveOpened}` : ''}, worst{' '}
          {Math.round(stats.worstBehindMs)}ms
        </span>
      )}
    </div>
  );
};

export default FrameOrderRow;
